import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';
import './PetalsRain.css';

/**
 * PetalsRain 组件
 * 
 * 功能：
 * - 在整个屏幕上渲染持续的、随机的玫瑰花瓣雨
 * - 使用 Three.js 粒子系统实现
 * - 花瓣具有随机的大小、旋转角度和飘落速度
 * - 飘落过程中伴随旋转和摆动效果
 * 
 * 技术实现：
 * - 使用 InstancedMesh 优化性能
 * - 自定义花瓣形状（使用平面几何体）
 * - 随机生成花瓣的初始位置、速度和旋转
 * 
 * 性能优化：
 * - 使用 InstancedMesh 一次性渲染大量花瓣
 * - 每个花瓣独立控制变换矩阵
 * - 重用超出屏幕的花瓣以减少对象创建
 */

interface PetalsRainProps {
  heartRate: number; // 心跳速率，影响花瓣飘落速度
}

const PetalsRain: React.FC<PetalsRainProps> = ({ heartRate }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const petalsRef = useRef<THREE.InstancedMesh | null>(null);
  const petalDataRef = useRef<{
    positions: Float32Array;
    velocities: Float32Array;
    rotations: Float32Array;
    rotationSpeeds: Float32Array;
    sizes: Float32Array;
  } | null>(null);
  const requestRef = useRef<number | null>(null);
  
  // 玫瑰花瓣颜色（深粉红到浅粉红）
  const petalColors = [
    new THREE.Color(0xff69b4), // hotpink
    new THREE.Color(0xff1493), // deeppink
    new THREE.Color(0xffb6c1), // lightpink
    new THREE.Color(0xff6b81), // dark pink
    new THREE.Color(0xffd1dc), // pastel pink
  ];

  /**
   * 初始化 Three.js 场景
   */
  useEffect(() => {
    if (!containerRef.current) return;

    // 创建场景
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // 创建相机
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    camera.position.z = 10;
    cameraRef.current = camera;

    // 创建渲染器
    const renderer = new THREE.WebGLRenderer({
      alpha: true, // 透明背景
      antialias: true
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // 创建花瓣
    createPetals(scene, renderer);

    // 响应窗口大小变化
    const handleResize = () => {
      if (!cameraRef.current || !rendererRef.current) return;
      cameraRef.current.aspect = window.innerWidth / window.innerHeight;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', handleResize);

    // 动画循环
    animate();

    return () => {
      window.removeEventListener('resize', handleResize);
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
      if (containerRef.current && renderer.domElement) {
        containerRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, []);

  /**
   * 创建花瓣粒子系统
   */
  const createPetals = (scene: THREE.Scene, renderer: THREE.WebGLRenderer) => {
    const petalCount = 150; // 花瓣数量
    
    // 创建花瓣形状（使用圆形平面）
    const geometry = new THREE.CircleGeometry(0.1, 8); // 半径 0.1，8 个分段
    
    // 创建材质
    const material = new THREE.MeshBasicMaterial({
      color: petalColors[Math.floor(Math.random() * petalColors.length)],
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.8
    });

    // 创建 InstancedMesh
    const petals = new THREE.InstancedMesh(geometry, material, petalCount);
    scene.add(petals);
    petalsRef.current = petals;

    // 初始化花瓣数据
    const positions = new Float32Array(petalCount * 3);
    const velocities = new Float32Array(petalCount * 2); // x, y 速度
    const rotations = new Float32Array(petalCount * 3); // x, y, z 旋转角度
    const rotationSpeeds = new Float32Array(petalCount * 3); // x, y, z 旋转速度
    const sizes = new Float32Array(petalCount); // 花瓣大小

    const dummy = new THREE.Object3D();
    const color = new THREE.Color();

    for (let i = 0; i < petalCount; i++) {
      // 随机初始位置
      positions[i * 3] = (Math.random() - 0.5) * 20; // x: -10 到 10
      positions[i * 3 + 1] = Math.random() * 15 - 7.5; // y: -7.5 到 7.5
      positions[i * 3 + 2] = (Math.random() - 0.5) * 10; // z: -5 到 5

      // 随机速度（向下飘落）
      velocities[i * 2] = (Math.random() - 0.5) * 0.02; // x: -0.01 到 0.01
      velocities[i * 2 + 1] = -(Math.random() * 0.02 + 0.01); // y: -0.02 到 -0.01

      // 随机旋转角度
      rotations[i * 3] = Math.random() * Math.PI * 2; // x: 0 到 2π
      rotations[i * 3 + 1] = Math.random() * Math.PI * 2; // y: 0 到 2π
      rotations[i * 3 + 2] = Math.random() * Math.PI * 2; // z: 0 到 2π

      // 随机旋转速度
      rotationSpeeds[i * 3] = (Math.random() - 0.5) * 0.02; // x
      rotationSpeeds[i * 3 + 1] = (Math.random() - 0.5) * 0.02; // y
      rotationSpeeds[i * 3 + 2] = (Math.random() - 0.5) * 0.02; // z

      // 随机大小
      sizes[i] = Math.random() * 0.5 + 0.5; // 0.5 到 1.0

      // 设置初始变换
      dummy.position.set(positions[i * 3], positions[i * 3 + 1], positions[i * 3 + 2]);
      dummy.rotation.set(rotations[i * 3], rotations[i * 3 + 1], rotations[i * 3 + 2]);
      dummy.scale.set(sizes[i], sizes[i], sizes[i]);
      dummy.updateMatrix();
      petals.setMatrixAt(i, dummy.matrix);

      // 设置随机颜色
      const randomColor = petalColors[Math.floor(Math.random() * petalColors.length)];
      petals.setColorAt(i, randomColor);
    }

    petalDataRef.current = {
      positions,
      velocities,
      rotations,
      rotationSpeeds,
      sizes
    };

    petals.instanceMatrix.needsUpdate = true;
    petals.instanceColor!.needsUpdate = true;
  };

  /**
   * 动画循环
   */
  const animate = () => {
    requestRef.current = requestAnimationFrame(animate);

    if (!petalsRef.current || !petalDataRef.current || !rendererRef.current || !sceneRef.current || !cameraRef.current) {
      return;
    }

    const petals = petalsRef.current;
    const data = petalDataRef.current;
    const dummy = new THREE.Object3D();

    // 根据心跳速率调整速度倍数（心率越高，飘落越快）
    const speedMultiplier = heartRate / 60; // 基准 60 BPM

    for (let i = 0; i < 150; i++) {
      // 更新位置
      data.positions[i * 3] += data.velocities[i * 2] * speedMultiplier; // x
      data.positions[i * 3 + 1] += data.velocities[i * 2 + 1] * speedMultiplier; // y

      // 添加左右摆动效果
      data.positions[i * 3] += Math.sin(Date.now() * 0.001 + i) * 0.005 * speedMultiplier;

      // 更新旋转
      data.rotations[i * 3] += data.rotationSpeeds[i * 3] * speedMultiplier;
      data.rotations[i * 3 + 1] += data.rotationSpeeds[i * 3 + 1] * speedMultiplier;
      data.rotations[i * 3 + 2] += data.rotationSpeeds[i * 3 + 2] * speedMultiplier;

      // 检查是否超出屏幕底部，重置到顶部
      if (data.positions[i * 3 + 1] < -8) {
        data.positions[i * 3] = (Math.random() - 0.5) * 20; // 随机 x 位置
        data.positions[i * 3 + 1] = 8; // 重置到顶部
        data.positions[i * 3 + 2] = (Math.random() - 0.5) * 10; // 随机 z 位置
        
        // 重置旋转
        data.rotations[i * 3] = Math.random() * Math.PI * 2;
        data.rotations[i * 3 + 1] = Math.random() * Math.PI * 2;
        data.rotations[i * 3 + 2] = Math.random() * Math.PI * 2;
      }

      // 更新变换矩阵
      dummy.position.set(data.positions[i * 3], data.positions[i * 3 + 1], data.positions[i * 3 + 2]);
      dummy.rotation.set(data.rotations[i * 3], data.rotations[i * 3 + 1], data.rotations[i * 3 + 2]);
      dummy.scale.set(data.sizes[i], data.sizes[i], data.sizes[i]);
      dummy.updateMatrix();
      petals.setMatrixAt(i, dummy.matrix);
    }

    petals.instanceMatrix.needsUpdate = true;
    rendererRef.current.render(sceneRef.current, cameraRef.current);
  };

  return <div ref={containerRef} className="petals-rain" />;
};

export default PetalsRain;